import { calculateRate } from '@_api/businessRateCalculate';
import { CalculatorApiResponse } from '@_types/_api-response';
import { scrollToOnMobile } from '@helpers/calculators';
import { currencyToNumber, getClassFunction } from '@helpers/functions';

const getClass = getClassFunction('kf-o-business-rates-calculator');
const getClassInput = getClassFunction('kf-o-business-rates-calculator-input');

window.KFBusinessRatesCalculator = window.KFBusinessRatesCalculator || {
  element: document.querySelector(getClass()),
  isInitialized: false,
  init() {
    if (this.isInitialized) return;
    this.isInitialized = true;
    if (!this.element) return;
    this.setUpCalculator(this.element);
  },
  setUpCalculator(el: Element) {
    if (!el) return;

    let form = el.querySelector<HTMLFormElement>(getClassInput('main'));
    const resultEl = el.querySelector<HTMLElement>(getClass('calculator-results__data'));
    const resultDetailEl = el.querySelector<HTMLElement>(getClass('calculator-results-detail'));
    const resetBtn = el.querySelector<HTMLElement>(getClassInput('reset-button .kf-a-button'));
    const locationDropdown = el.querySelector<HTMLSelectElement>(getClassInput('main .kf-a-dropdown'));
    const prevRvEl = el.querySelector<HTMLInputElement>(getClassInput('prev-value'));
    const isPrevRatingRadioEl = el.querySelector<HTMLElement>(getClassInput('is-prev-rating .kf-m-radio-group'));
    const isOccupiedRadioEl = el.querySelector<HTMLElement>(getClassInput('is-property-occupied .kf-m-radio-group'));

    if (form) {
      const validateList = ['location', 'prevRv', 'currentRv'];

      const checkButtonAvailability = (list?: string[]) => {
        let valid = true;

        form = el.querySelector<HTMLFormElement>(getClassInput('main'));
        if (!form) return false;
        const formData = new FormData(form);
        const isProperty2017No = formData.get('inPreviousRatingList') === '2';

        (list ?? validateList).forEach((name) => {
          const value = formData.get(name);
          const el = form?.querySelector<HTMLElement>(`[data-name="${name}"]`);

          if (name === 'prevRv' && isProperty2017No) {
            el?.classList.remove('show-error');

            return;
          }

          if (!value) {
            valid = false;
            el?.classList.add('show-error');
          } else {
            el?.classList.remove('show-error');
          }
        });

        return valid;
      };

      function getFormValues(formData: FormData) {
        let checkList = validateList;

        if (formData.get('inPreviousRatingList') === '2') {
          checkList = checkList.filter((item) => item !== 'prevRv');
        }
        const isValid = checkButtonAvailability(checkList);
        const inPreviousRatingList = formData.get('inPreviousRatingList') === '1';
        let rv = currencyToNumber(formData.get('currentRv')?.toString()) ?? 0,
          prevRv = inPreviousRatingList ? currencyToNumber(formData.get('prevRv')?.toString(), 0) : 0,
          location = formData.get('location')?.toString() || undefined;

        if (!isValid) {
          rv = 0;
          prevRv = 0;
          location = undefined;
        }
        const isOccupied = formData.get('isOccupied') === '1';
        const isOccupyDisabled = isOccupiedRadioEl?.classList.contains('disabled');

        return {
          isValid,
          inPreviousRatingList,
          rv,
          prevRv,
          location,
          isOccupied,
          isOccupyDisabled,
        };
      }

      function renderResult(data: CalculatorApiResponse, isValid: boolean) {
        if (data.success) {
          if (resultEl) {
            window.dispatchEvent(new CustomEvent('reloadCalculator', { detail: data.data }));
            // scroll to the result section when screen width < 768px
          }
          if (resultDetailEl) {
            resultDetailEl.classList.remove('hide');
            resultDetailEl.innerHTML = '';
            const script = document.createElement('script');

            script.dataset.rct = 'businessCalculatorResultDetail';
            script.type = 'application/json';
            const textNode = document.createTextNode(JSON.stringify({ ...data.data, reset: !isValid }));

            script.appendChild(textNode);
            const shouldScroll = data.data && isValid && resultEl;

            if (shouldScroll) {
              scrollToOnMobile(resultEl);
            }
            resultDetailEl.appendChild(script);
          }
        }
      }

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const { isValid, inPreviousRatingList, rv, prevRv, location, isOccupied, isOccupyDisabled } = getFormValues(formData);

        const dataParams = {
          contentGuid: formData.get('contentGuid')?.toString() || undefined,
          location: location,
          rv,
          inPreviousRatingList,
          prevRv,
          situation: isOccupied ? 'Occupied' : 'Unoccupied',
        };

        const data = await calculateRate({ data: dataParams });

        window.tC?.event?.business_rates_calculator?.(e, {
          page_path: window.document.location,
          property_occupied: isOccupyDisabled ? null : isOccupied ? 'Yes' : 'No',
          property_location: location ?? null,
        });

        renderResult(data, isValid);

        resetBtn?.classList.remove('hide');
        window.dispatchEvent(new Event('calculatorReload'));

        return false;
      });

      locationDropdown?.addEventListener('dropdown-change', (e: CustomEvent<{ dataset?: { isDisabledOccupy?: boolean } }>) => {
        checkButtonAvailability(['location']);
        if (e.detail?.dataset?.isDisabledOccupy) {
          isOccupiedRadioEl?.classList.add('disabled');
        } else {
          isOccupiedRadioEl?.classList.remove('disabled');
        }
      });
      if (isPrevRatingRadioEl) {
        isPrevRatingRadioEl.addEventListener('radio-change', (e: Event) => {
          const customEvent = e as CustomEvent<{ value: string }>;
          const value = customEvent.detail.value;

          if (value === '2') {
            prevRvEl?.classList.add('v-hidden');
          } else {
            prevRvEl?.classList.remove('v-hidden');
          }
        });
      }
    }
  },
};

window.KFBusinessRatesCalculator.init();

export {};
